import i18n from '@/libs/i18n'

/**
 * Money crosses the wire as an integer number of cents and is only ever turned into
 * a decimal for display. Nothing in this app does arithmetic on the formatted value.
 */
export const formatAmount = (cents?: number | null, currency = 'EUR'): string => {
    const value  = (cents ?? 0) / 100
    const locale = i18n.global.locale.value

    // An EMPTY code is not „no argument“ — the default above does not apply to it — and
    // Intl refuses it with a RangeError. Thrown in a template that takes the whole screen
    // with it: an event with no card yet answered `currency: ""` and its finance tab drew
    // nothing. So the empty code is the house's default, and a code Intl does not know is
    // printed beside a plain number rather than thrown.
    try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'EUR' }).format(value)
    } catch {
        return `${new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)} ${currency}`.trim()
    }
}

/** Parses a user-typed amount into cents. Rounds, so 12.345 becomes 1235, not 1234. */
export const toCents = (amount?: number | null): number => Math.round((amount ?? 0) * 100)

/** Cents to the decimal an input field binds to. */
export const fromCents = (cents?: number | null): number => (cents ?? 0) / 100

/**
 * The fixed denominator of a stored exchange rate: how many millionths of one unit of
 * the house's currency one unit of the foreign one buys. 1.176500 is 1_176_500.
 *
 * Mirrors DataStructures.ExchangeRateScale on the API side. A millionth rather than a
 * basis point because a basis point carries four decimal places, which is plenty near
 * one and useless below it — a currency worth 0.0025 has two significant digits there.
 */
export const EXCHANGE_RATE_SCALE = 1_000_000

/** Parses a rate a receptionist typed into the stored integer. */
export const toRate = (rate?: number | null): number => Math.round((rate ?? 0) * EXCHANGE_RATE_SCALE)

/** A stored rate as the number an input field binds to. */
export const fromRate = (rate?: number | null): number => (rate ?? 0) / EXCHANGE_RATE_SCALE

/**
 * Converts an amount at a stored rate, the same way the server does.
 *
 * Half away from zero, once, on the total. It has to agree with
 * DataStructures.ConvertAtRate to the cent: the server recomputes this and refuses the
 * request when the two disagree, so a screen rounding differently would show a figure
 * and then be refused for it.
 *
 * Done in integers rather than on the decimal the field holds, for the reason the whole
 * money path is: 630.82 * 1.1765 in a float is not what it looks like.
 */
export const convertAtRate = (amount?: number | null, rate?: number | null): number => {
    const cents = amount ?? 0
    const scaled = rate ?? 0

    if (scaled <= 0 || cents === 0) return 0

    const sign = cents < 0 ? -1 : 1

    return sign * Math.floor((Math.abs(cents) * scaled + EXCHANGE_RATE_SCALE / 2) / EXCHANGE_RATE_SCALE)
}

/**
 * The VAT rates a house offers before anybody has changed them, in basis points —
 * mirroring DataStructures.DefaultVATRates on the API side: the Slovenian set, with
 * zero for what is outside VAT (the tourist tax is the everyday example).
 */
export const DEFAULT_VAT_RATES = [2200, 950, 500, 0]
